import React, { useEffect, useMemo, useRef, useState } from 'react';
import GeneralTooltips from 'components/Dashboard/components/GeneralTooltips';
import ReactGA from 'react-ga4';
import { useHeightContainer } from 'components/Dashboard/utilities/useHeightContainer';
import validateParameter from 'utils/validateParameter';
import { useCreateAnalyticsSections } from 'components/Dashboard/Analytics/ui/AnalyticsSidebar';
import GraphItem from 'components/Dashboard/Analytics/AnalyticsDashboardGraphItem';
import Loading from 'components/Login/Loading';
import useGeneralApiCall from 'components/Dashboard/apiCalls/useGeneralApiCall';
import useTopicOrClientWord from 'components/Dashboard/hooks/useTopicOrClientWord';
import { mentionsGraphNations } from 'components/Dashboard/Analytics/createAnalyticsState';
import normalize from 'components/Dashboard/utilities/normalizeString';
import CustomScrollbar from 'components/Common/CustomScrollbar';
import { hex_is_light } from 'components/Reports/ui/ReportDesignButton';
import { VocalWidgetContainer } from 'components/Dashboard/Analytics/ui/AnalyticsDashboard/MostVocalWidget';
import FeedNoAvailable from 'components/embed/FeedNoAvailable';
import EmbedAnalyticsIndividual from './EmbedAnalyticsIndividual';
import TitleWidget from './EmbedAnalyticsTitleWidget';

const EmbedAnalytics = (props) => {
  const { analyticsSections } = useCreateAnalyticsSections();
  const {
    selectedTopics,
    chartTypesSelected,
    isFromPopup,
    keywordsLists,
    selectedCountriesProps,
    designState,
    searchValue,
    header: headerProps,
    subheader: subheaderProp,
    scrollBarPositionState,
    isLoadingContent,
  } = props;

  const { generalApiCall } = useGeneralApiCall();
  const { agencyUser } = useTopicOrClientWord();
  const [analyticsKeywordsLists, setAnalyticsKeywordsLists] = useState(
    keywordsLists ? keywordsLists.filter((list) => topicsIds.includes(list.id)) : []
  );

  const embedState = {
    topicsIds:
      selectedTopics ??
      (validateParameter('topic-id')
        ? [validateParameter('topic-id')]
        : validateParameter('topicsIds')
            ?.split(',')
            ?.map((item) => parseInt(item))),
    chartTypes:
      chartTypesSelected ??
      (validateParameter('type') ? [validateParameter('type')] : validateParameter('chartTypes')?.split(',')),
    headerColour:
      designState?.headerColour ??
      (validateParameter('titleColor') ? validateParameter('titleColor') : validateParameter('headerColour')),
    background: designState?.backgroundColour ?? validateParameter('backgroundColour'),
    includeBranding: designState?.includeBranding ?? validateParameter('includeBranding'),
    agency: agencyUser || !!validateParameter('agency'),
    header: headerProps ?? (!!validateParameter('header') ? validateParameter('header') : `Live political data`),
    search: searchValue ?? validateParameter('search'),
    subheader:
      subheaderProp ??
      (!!validateParameter('subheader')
        ? validateParameter('subheader')
        : `Real-time political data ${agencyUser ? 'relevant' : 'related'} to ${analyticsKeywordsLists?.[0]?.label}`),
  };

  const { topicsIds, chartTypes, background, headerColour, agency, search, header, subheader } = embedState;
  const [analyticsPlan, setAnalyticsPlan] = useState({});
  const { embedAnalyticsWidgetsAllowed, removeAnalyticsWidgetBrandingAllowed, tweetsEnabled } = analyticsPlan ?? {};

  const sections =
    chartTypes[0] === 'Dashboard'
      ? analyticsSections
      : chartTypes.map((item) => {
          return analyticsSections.find((chart) => chart.name === item);
        });
  const includeBranding = removeAnalyticsWidgetBrandingAllowed ? embedState?.includeBranding : true;
  const singleGraph = sections.length === 1;

  const [topic, setTopic] = useState(
    keywordsLists ? keywordsLists.find((item) => parseInt(item.id) === parseInt(topicsIds[0])) : {}
  );
  const selectedCountries = useMemo(
    () =>
      selectedCountriesProps ??
      mentionsGraphNations.filter((item) => validateParameter('nations')?.split('-').includes(normalize(item.value))),
    [selectedCountriesProps]
  );

  const [isLoading, setIsLoading] = useState(true);
  const [heightContainer, containerRef] = useHeightContainer();
  const [scrollBarPosition, setScrollBarPosition] = useState(0);
  const [currentChart, setCurrentChart] = useState(singleGraph ? sections[0]?.name : null);

  const teamId = props?.teamId ?? validateParameter('teamId');

  const scrollbarContentContainer = useRef(null);
  const addScrollClass = () => {
    let el = scrollbarContentContainer?.current?.contentElement?.parentElement;
    if (el?.scrollHeight > el?.clientHeight) {
      el?.classList?.add('element-with-scroll');
    } else {
      el?.classList?.remove('element-with-scroll');
    }
  };

  const loadLists = useRef(null);
  loadLists.current = async (source) => {
    try {
      setIsLoading(true);
      if (teamId) {
        if (!keywordsLists) {
          const pathname = `/api/widget/team-topics-for-analytics?teamId=${teamId}&topicIds=${topicsIds.join(',')}`;
          const method = 'get';
          const results = await generalApiCall({ pathname, method, requestSource: source, notShowErrorMessage: true });
          if (!!results) {
            let lists = results.map((item) => {
              return { ...item, name: item.label, id: item.value };
            });
            setAnalyticsKeywordsLists(lists);
            setTopic(lists.find((item) => parseInt(item.id) === parseInt(topicsIds[0])));
            setIsLoading(false);
            const analyticsPlan = await analyticsPlanCall(source);
            if (!!analyticsPlan) {
              setAnalyticsPlan(analyticsPlan);
            }
            addScrollClass();
          }
        }
      } else {
        setIsLoading(false);
        addScrollClass();
      }
    } catch (error) {}
  };

  const analyticsPlanCall = async (source) => {
    const method = 'get';
    const pathname = `/api/widget/plan?team-id=${teamId}`;
    const results = await generalApiCall({ method, pathname, requestSource: source, notShowErrorMessage: true });
    return results;
  };

  useEffect(() => {
    const source = new AbortController();
    loadLists.current();
    return () => {
      source.abort('Embed analytics cancelled by the user');
    };
  }, []);

  useEffect(() => {
    if (keywordsLists) {
      setAnalyticsKeywordsLists(keywordsLists.filter((list) => topicsIds.includes(list.id)));
      setTopic(keywordsLists.find((item) => parseInt(item.id) === parseInt(topicsIds[0])));
    }
  }, [selectedTopics]);

  useEffect(() => {
    addScrollClass();
  }, [isLoading, chartTypes]);

  return (
    <>
      <div ref={containerRef} style={{ height: isFromPopup ? '100%' : `${heightContainer - 2}px` }}>
        <div className={`embed-content h-100`}>
          <div className='dashboard-root-container rounded border analytics-embed-container'>
            {isLoading ? (
              <div className='flex-grow-1 position-relative'>
                <Loading />
              </div>
            ) : (
              <>
                {embedAnalyticsWidgetsAllowed ? (
                  <>
                    <div style={{ background: headerColour, color: hex_is_light(headerColour) ? '#00122b' : '#fff' }}>
                      <TitleWidget
                        isFromPopup={isFromPopup}
                        selectedTopics={selectedTopics ?? analyticsKeywordsLists}
                        topic={topic}
                        setTopic={setTopic}
                        analyticsKeywordsLists={analyticsKeywordsLists}
                        agencyUser={agency}
                        header={header}
                        subheader={subheader}
                      />
                    </div>
                    <div className='flex-grow-1' style={{ background: background }}>
                      {isLoadingContent ? (
                        <Loading />
                      ) : (
                        <>
                          {currentChart ? (
                            <EmbedAnalyticsIndividual
                              topic={topic}
                              chart={currentChart}
                              setCurrentChart={setCurrentChart}
                              teamId={teamId}
                              singleGraph={singleGraph}
                              tweetsEnabled={tweetsEnabled}
                            />
                          ) : (
                            <CustomScrollbar
                              className={'simple-scrollbar'}
                              maximalThumbYSize={100}
                              ref={scrollbarContentContainer}
                              onScroll={(scrollValues) => {
                                setScrollBarPosition(scrollValues?.scrollTop);
                              }}
                            >
                              <div className={`p-4 row mx-0`}>
                                {sections.map((section, index) => {
                                  const { name } = section;
                                  const mostVocal = name === 'Most vocal policymakers';
                                  const stakeholdersGraph = name === 'Top MPs and peers';
                                  const restrictedSection = mostVocal || stakeholdersGraph;
                                  return (
                                    <div
                                      key={`item-container-${name}-${index}`}
                                      className={`${singleGraph ? 'col-lg-16 ' : 'col-lg-8 mb-4 '}  dashboard-analytics px-3 ${restrictedSection ? '' : 'pointer'}`}
                                      style={singleGraph ? { height: '47vh' } : { minHeight: '375px' }}
                                      onClick={() => {
                                        if (!restrictedSection) {
                                          setCurrentChart(name);
                                        }
                                      }}
                                    >
                                      {name === 'Most vocal policymakers' ? (
                                        <VocalWidgetContainer
                                          search={search}
                                          topic={topic}
                                          scrollBarPosition={scrollBarPosition}
                                          teamId={teamId}
                                          isFromPopup={isFromPopup}
                                        />
                                      ) : (
                                        <div
                                          className={`box-outstanding dashboard-root-container p-4 position-relative box-rounded trends-ink-box h-100 ${singleGraph ? '' : 'mb-4'}`}
                                        >
                                          <h3 className='title-h5-m-bold mb-4 position-relative z-max'>{name}</h3>
                                          <div className='flex-grow-1'>
                                            <GraphItem
                                              isFromPopup={isFromPopup}
                                              key={`item-${name}-${topic?.id}`}
                                              topic={topic}
                                              chartTypesSelected={chartTypesSelected}
                                              chartType={name}
                                              teamId={teamId}
                                              selectedCountriesProps={selectedCountries}
                                              scrollBarPosition={
                                                !!scrollBarPositionState?.scrollBarPosition
                                                  ? scrollBarPositionState?.scrollBarPosition
                                                  : scrollBarPosition
                                              }
                                              totalHeight={singleGraph}
                                              tweetsEnabled={tweetsEnabled}
                                            />
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </CustomScrollbar>
                          )}
                        </>
                      )}
                    </div>
                    {(typeof includeBranding === 'string' ? includeBranding === 'true' : includeBranding) && (
                      <div className='pr-4 text-right py-3 border-top' style={{ background: background }}>
                        <a
                          className='text-decoration-none text-main-blue'
                          href={`${import.meta.env.VITE_APP_APPURL}`}
                          target='_blank'
                          rel='noopener noreferrer'
                          style={{ color: hex_is_light(background) ? '#00122b' : '#fff' }}
                        >
                          Political analytics powered by{' '}
                          <img
                            src={`${import.meta.env.VITE_APP_CDNURL}/images/logo${hex_is_light(background) ? '' : '_white'}.svg`}
                            alt='PolicyMogul logo'
                            width={'90px'}
                            className={`d-inline-block ml-1`}
                          />{' '}
                        </a>
                      </div>
                    )}
                  </>
                ) : (
                  <FeedNoAvailable />
                )}
              </>
            )}
          </div>
        </div>
      </div>
      <GeneralTooltips />
    </>
  );
};

const EmbedMain = () => {
  useEffect(() => {
    document.getElementsByTagName('body')[0].classList.add('embed-widget-body');
    ReactGA.initialize(`${import.meta.env.VITE_APP_WIDGET_ANALYTICSCODE}`);
    ReactGA.send({
      hitType: 'pageview',
      page: window.location.pathname + window.location.search,
    });
  }, []);

  return <>{<EmbedAnalytics />}</>;
};

export { EmbedAnalytics, TitleWidget };
export default EmbedMain;
